import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import type { OrgProfile } from "@/components/org-settings/types";
import { resolveCurrentPage } from "@/lib/page-tree";
import type { Block, Page, Site, UnitType } from "@/types/database";

// Public unit info for site rendering. name/location come from org_profiles
// (unit_name/unit_slug are snapshots of Clerk's own org name/slug, since
// anonymous site visitors have no Clerk session to read those live); unit
// type/number come from the shared unit_details record via the public
// gateway RPC (see get_public_site_unit_identity), keeping them in sync with
// the hub's Details page. The legacy `units` table is retired — there is no
// local FK to a units row anymore, org_profiles.clerk_org_id is the join key.
export interface PublicUnitInfo {
  name: string;
  unitType: UnitType | null;
  unitIdentifier: string | null;
  location: string | null;
}

export interface PublicSiteData {
  unit: PublicUnitInfo;
  slug: string | null;
  site: Site;
  pages: Page[];
  currentPage: Page | null;
  blocks: Block[];
  // The org's own custom domain, only once Heroku ACM has actually issued
  // its certificate — see getCanonicalOrigin in lib/seo/canonical-origin.ts.
  // Looked up regardless of which identifier resolved this request, so a
  // visitor on the default /site/:slug path still gets metadata pointing at
  // the real canonical (custom-domain) origin once one is active.
  activeDomainHostname: string | null;
}

// Default routing is by the org's own Clerk slug (org_profiles.unit_slug,
// snapshotted at profile-creation time — same "snapshot on write" pattern
// as the hub/calendar public_calendar_slug). A verified/active custom
// domain in org_domains overrides that, resolved by hostname instead.
export type PublicSiteIdentifier =
  | { type: "slug"; slug: string }
  | { type: "domain"; hostname: string };

async function resolveClerkOrgId(identifier: PublicSiteIdentifier): Promise<string | null> {
  if (identifier.type === "domain") {
    // org_domains has no anon SELECT policy (only authenticated org-member
    // ones) — this goes through the SECURITY DEFINER RPC added in
    // 20260716130000_public_domain_resolution_functions.sql instead of a
    // direct table read, which would silently return zero rows here.
    const { data, error } = await supabase.rpc("get_org_id_for_verified_domain" as any, {
      target_domain_name: identifier.hostname,
    });
    if (error || !data) return null;
    return data as unknown as string;
  }

  const { data, error } = await supabase
    .from("org_profiles" as any)
    .select("clerk_org_id")
    .eq("unit_slug", identifier.slug)
    .single();
  if (error || !data) return null;
  return (data as unknown as { clerk_org_id: string }).clerk_org_id;
}

export function usePublicSite(identifier: PublicSiteIdentifier | null, pageSlug: string | undefined) {
  const queryKeyIdentifier = identifier ? [identifier.type, identifier.type === "slug" ? identifier.slug : identifier.hostname] : null;

  return useQuery({
    queryKey: ["public-site", queryKeyIdentifier, pageSlug],
    enabled: Boolean(identifier),
    queryFn: async (): Promise<PublicSiteData | null> => {
      if (!identifier) return null;

      const clerkOrgId = await resolveClerkOrgId(identifier);
      if (!clerkOrgId) return null;

      const { data: orgProfileData, error: orgProfileError } = await supabase
        .from("org_profiles" as any)
        .select("*")
        .eq("clerk_org_id", clerkOrgId)
        .single();
      if (orgProfileError || !orgProfileData) return null;
      const orgProfile = orgProfileData as unknown as OrgProfile;

      const { data: siteData } = await supabase
        .from("sites" as any)
        .select("*")
        .eq("clerk_org_id", clerkOrgId)
        .single();
      if (!siteData) return null;
      const site = siteData as unknown as Site;

      const { data: pagesData } = await supabase
        .from("pages")
        .select("*")
        .eq("site_id", site.id)
        .order("page_order");
      const pages = (pagesData ?? []) as Page[];
      const currentPage = resolveCurrentPage(pages, pageSlug);

      let blocks: Block[] = [];
      if (currentPage) {
        const { data: blocksData } = await supabase
          .from("blocks")
          .select("*")
          .eq("page_id", currentPage.id)
          .order("block_order");
        blocks = (blocksData ?? []) as Block[];
      }

      // Unit number/type come from the shared unit_details record (the hub's
      // Details page is their system of record), read anonymously through the
      // public gateway RPC — not from org_profiles' now-vestigial copy.
      const { data: identityData } = await supabase.rpc("get_public_site_unit_identity" as any, {
        target_org_id: clerkOrgId,
      });
      const identity =
        ((identityData ?? []) as unknown as Array<{ unit_type: UnitType | null; unit_number: string | null }>)[0] ?? null;

      const unit: PublicUnitInfo = {
        name: orgProfile.unit_name ?? "Unnamed unit",
        unitType: identity?.unit_type ?? orgProfile.unit_type,
        unitIdentifier: identity?.unit_number ?? null,
        location: orgProfile.location_display,
      };

      const { data: activeDomainData } = await supabase.rpc("get_active_domain_for_org" as any, {
        target_clerk_org_id: clerkOrgId,
      });
      const activeDomainHostname = (activeDomainData as unknown as string | null) ?? null;

      return { unit, slug: orgProfile.unit_slug, site, pages, currentPage, blocks, activeDomainHostname };
    },
  });
}
